import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { SessionView } from "../components/session/SessionView";
import { PageHeader } from "../components/shell/PageHeader";
import { EmptyState, ErrorNotice, SkeletonRows } from "../components/ui/Feedback";
import { Freshness } from "../components/ui/Freshness";
import { SearchField, SelectField } from "../components/ui/Input";
import { RowState, StatusMark } from "../components/ui/Status";
import { Cell, Pagination, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import type { Column } from "../components/ui/Table";
import { Metric, MicroLabel } from "../components/ui/Text";
import { getPage } from "../lib/api";
import { useViewParam } from "../lib/useViewParam";
import { count, kwh, since, timestamp } from "../lib/format";
import {
  anomalyLabel,
  displayEnergy,
  kilowatts,
  sessionDuration,
  sessionStateTone,
} from "../lib/sessions";
import type { Session } from "../lib/types";

/**
 * Charging sessions — spec 6.5's accounting records, as they are produced.
 *
 * Live and closed sessions share one table rather than being split across two
 * pages or two tabs. The operator's question is "what is happening on my site",
 * and a truck that started charging four minutes ago is the most interesting row
 * on the page — putting it behind a filter would hide the newest thing.
 *
 * What keeps that honest is the energy column: an open session's figure is an
 * estimate from interim readings (spec 6.3) and is drawn as one. See
 * `lib/sessions.ts`.
 *
 * The columns are weighted, not uniform. State leads because a live or abandoned
 * session is what an operator scans for; energy is the one figure set heavy; who
 * and where read at body weight; when, peak and stop reason recede, because the
 * list is already in time order and they matter only once a row is opened. A
 * flagged row says what is wrong in words, in the signal colour, at the row's end.
 *
 * A row opens the session over this list (`?session=`), so the filters and page
 * the operator was on survive the look.
 */

const COLUMNS: readonly Column[] = [
  { key: "state", label: "State", width: "9%" },
  { key: "identity", label: "Identity", width: "19%" },
  { key: "station", label: "Charge point", width: "15%" },
  { key: "energy", label: "kWh", numeric: true, width: "10%" },
  { key: "duration", label: "Duration", numeric: true, width: "9%" },
  { key: "started", label: "Started", width: "14%" },
  { key: "peak", label: "Peak kW", numeric: true, width: "8%" },
  { key: "flags", label: "Notes" },
];

const POLL_MS = 15_000;

export function Sessions() {
  const [params, setParams] = useSearchParams();
  const [viewing, setViewing] = useViewParam("session");

  const chargePointId = params.get("chargePointId") ?? "";
  const state = params.get("state") ?? "";
  /**
   * Not a form field: it is a uuid, which nobody types. It arrives from a link on
   * an identity's page, so the filter is shown as something to clear rather than
   * something to fill in.
   */
  const identityId = params.get("identityId") ?? "";
  const page = positiveInt(params.get("page")) ?? 1;

  const sessions = useQuery({
    queryKey: ["sessions", { chargePointId, identityId, state, page }],
    queryFn: () =>
      getPage<Session>("/sessions", { query: { chargePointId, identityId, state, page } }),
    /**
     * Polled, because the table holds open sessions whose energy and duration are
     * moving. Slower than the fleet view's cadence: a session's figures change on
     * the station's sample interval, which spec 4.4 pushes as 60 s, so a faster
     * poll would mostly re-fetch identical rows.
     */
    refetchInterval: POLL_MS,
    placeholderData: (previous) => previous,
  });

  const rows = sessions.data?.rows ?? [];
  const now = Date.now();
  const live = rows.filter((row) => row.state === "active");

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Sessions"
        metrics={
          <>
            <Metric
              label="Total"
              value={count(sessions.data?.meta?.total)}
              hint="matching filters"
            />
            <Metric
              label="Charging now"
              value={count(live.length)}
              hint="on this page"
              tone={live.length > 0 ? "signal" : "muted"}
            />
            <Metric
              label="Delivered"
              value={kwh(rows.reduce((total, row) => total + (row.energyWh ?? 0), 0))}
              unit="kWh"
              hint="committed, this page"
            />
          </>
        }
        freshness={
          <Freshness
            updatedAt={sessions.dataUpdatedAt}
            isFetching={sessions.isFetching}
            intervalMs={POLL_MS}
          />
        }
        lede="A session's energy is committed when it closes. An open session shows a live estimate from its interim readings, which is never a billable figure."
      />

      <form
        onSubmit={(event) => event.preventDefault()}
        className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 lg:grid-cols-4"
      >
        <SearchField
          label="Charge point"
          placeholder="Exact charge point id"
          value={chargePointId}
          onChange={(event) => setFilter("chargePointId", event.target.value)}
          hint="The station id as it connects, not a search."
        />
        <SelectField
          label="State"
          value={state}
          onChange={(event) => setFilter("state", event.target.value)}
        >
          {/* The three values `charging_session_state_check` permits, and no
              others — a filter that can never match a row is worse than none. */}
          <option value="">Any</option>
          <option value="active">Active</option>
          <option value="ended">Ended</option>
          <option value="abandoned">Abandoned</option>
        </SelectField>
      </form>

      {identityId === "" ? null : (
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-l-2 border-signal bg-signal-wash/40 px-4 py-3">
          <MicroLabel>Filtered to one identity</MicroLabel>
          <span className="text-body text-ink">
            {rows[0]?.identityLabel ?? "This card"} — showing only its sessions.
          </span>
          <button
            type="button"
            onClick={() => setFilter("identityId", "")}
            className="text-micro font-semibold uppercase tracking-[0.11em] text-signal underline decoration-1 underline-offset-4 hover:no-underline"
          >
            Show all
          </button>
        </div>
      )}

      {sessions.isError ? (
        <ErrorNotice error={sessions.error} onRetry={() => void sessions.refetch()} />
      ) : null}

      <div className="flex flex-col gap-3">
        <TableFrame>
          <TableHead columns={COLUMNS} />
          {sessions.isPending ? (
            <SkeletonRows rows={8} columns={COLUMNS.length} />
          ) : (
            <tbody>
              {rows.map((row, index) => {
                const energy = displayEnergy(row);
                return (
                  <TableRow
                    key={row.id}
                    index={index}
                    onActivate={() => setViewing(row.id)}
                    isFaulted={row.anomalyFlags.length > 0}
                  >
                    <Cell>
                      <RowState tone={sessionStateTone(row.state)}>{row.state}</RowState>
                    </Cell>
                    <Cell>
                      <div className="flex flex-col gap-0.5">
                        {/* The tag as presented is always shown. Spec 5 keeps it
                            verbatim because it is the evidence, and for an
                            unnamed card it is the only handle that exists. */}
                        <span
                          className={
                            row.identityLabel === null ? "text-ink-faint" : "font-medium text-ink"
                          }
                        >
                          {row.identityLabel ?? "Unknown card"}
                        </span>
                        <span className="font-mono text-micro tracking-normal normal-case text-ink-faint">
                          {row.idTag}
                        </span>
                      </div>
                    </Cell>
                    <Cell>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-ink">{row.chargePointId}</span>
                        <span className="text-micro tracking-normal normal-case text-ink-faint">
                          EVSE {row.evseId}
                        </span>
                      </div>
                    </Cell>
                    <Cell numeric>
                      {/* The row's one heavy figure. A tilde, not a colour, marks
                          the estimate: the distinction has to survive a greyscale
                          print of a chargeback report. */}
                      {energy.wh === null ? (
                        <span className="text-ink-faint">—</span>
                      ) : (
                        <span
                          className={`font-display text-section tabular-nums ${
                            energy.isEstimate
                              ? "font-medium text-ink-muted"
                              : "font-semibold text-ink"
                          }`}
                        >
                          {energy.isEstimate ? "~" : ""}
                          {kwh(energy.wh)}
                        </span>
                      )}
                    </Cell>
                    <Cell numeric className="tabular-nums">
                      {sessionDuration(row, now)}
                    </Cell>
                    <Cell>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-ink-muted">
                          {timestamp(row.startedAtCp, row.depotTimezone)}
                        </span>
                        <span className="text-micro tracking-normal normal-case text-ink-faint">
                          {since(row.startedAtCp, now)}
                        </span>
                      </div>
                    </Cell>
                    <Cell numeric muted className="tabular-nums">
                      {kilowatts(row.peakPowerW)}
                    </Cell>
                    <Cell muted>
                      {row.anomalyFlags.length === 0 ? (
                        (row.stopReason ?? "")
                      ) : (
                        <span className="inline-flex items-center gap-2 font-medium text-signal">
                          <StatusMark tone="faulted" />
                          {row.anomalyFlags.map(anomalyLabel).join(" · ")}
                        </span>
                      )}
                    </Cell>
                  </TableRow>
                );
              })}
            </tbody>
          )}
        </TableFrame>

        {!sessions.isPending && rows.length === 0 ? (
          <EmptyState
            title="No sessions match these filters."
            detail="A session appears the moment a station sends StartTransaction, and its energy is committed when the matching StopTransaction arrives."
          />
        ) : null}

        {rows.some((row) => row.state === "active") ? (
          <MicroLabel>
            A tilde marks an estimate from interim readings, not a committed figure.
          </MicroLabel>
        ) : null}

        <Pagination
          meta={sessions.data?.meta}
          shown={rows.length}
          onPage={(next) => {
            const nextParams = new URLSearchParams(params);
            if (next <= 1) nextParams.delete("page");
            else nextParams.set("page", String(next));
            setParams(nextParams);
          }}
        />
      </div>

      <SessionView
        sessionId={viewing}
        open={viewing !== ""}
        onOpenChange={(open) => {
          if (!open) setViewing("");
        }}
      />
    </>
  );
}

function positiveInt(raw: string | null): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined;
  const value = Number(raw);
  return value >= 1 ? value : undefined;
}
